/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import useLoadAlertHistory from '@ion/query/src/hooks/useLoadAlertHistory';
import {AlertRowEditor} from './AlertRowEditor';
import {useStyles} from './FileHistoryGrid';
import {getAlertConfiguration, viewAlertConfiguration} from './state';

export interface AlertHistoryGridProps {
  device?: Device;
  isRecentAlerts?: boolean;
  alertTime?: string;
  alertType?: string;
  refreshGrid?: boolean;
  setTotalRows?: (totalRows: number) => void;
  setSelectedAlerts?: Function;
  isActive?: boolean;
};

/**
 * Grid to be rendered on the File Tab under Device Details page
 */
export const AlertHistoryGrid = ({
  device,
  isRecentAlerts,
  alertTime,
  alertType,
  setTotalRows,
  setSelectedAlerts,
  isActive = true
}: AlertHistoryGridProps, ref) => {
  const classes                               = useStyles();
  const dispatch                              = useDispatch();
  const ionGridRef                            = useRef<IonGridAPI>();
  const [gridApi, setGridApi]                 = useState(null);
  const [hasItems, setHasItems]               = useState<boolean>(false);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const {globalGroup}                         = useSelector(({globalGroup}) => globalGroup);
  const getAlertHistory                       = useLoadAlertHistory({
    device,
    alertTime,
    alertType,
    isRecentAlerts,
    isActive,
    setTotalRows
  });

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState       : null
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    onRefresh
  }));

  const onColumnSelectionsUpdate = useCallback((columns: ColDef[]): void => {
    onColumnChooserSave(columns);
  }, []);

  /**
   * Takes a list of Alert history and constructs a list
   * that can be easily consumed by the IonGrid API
   */
  const constructAlertHistoryGridRows = useCallback((alerts: GenericObject[]): GridResult[] => {
    const gridItems = alerts?.map(alert => ({
      ...alert,
      alertConfigUI    : alert?.alertConfiguration?.title,
      createdOnUI      : formatDateTime(alert?.createdOn),
      acknowledgedBy   : alert?.acknowledgedUserName,
      acknowledgedOnUI : formatDateTime(alert?.acknowledgedOn),
      acknowledgedUI   : booleanToString(alert?.acknowledgedById)
    })) as GridResult[];
    setHasItems(!!gridItems.length);
    return gridItems;
  }, []);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(function(api) {
    setGridApi(api);
  }, []);

  const onRefresh = function() {
    setRefreshRequired(true);
    setTimeout(() => {
      setRefreshRequired(false);
    }, 1000);
  };

  const onAlertConfigClick = useCallback((alert: GenericObject) => {
    batch(() => {
      dispatch(viewAlertConfiguration(alert));
      dispatch(getAlertConfiguration(alert?.alertConfigurationId));
    });
  }, [dispatch]);

  const onSelectionChanged = useCallback((event) => {
    const rowsSelected = event.api.getSelectedRows();
    const uniqItems = getUniqItems(rowsSelected);
    setSelectedAlerts(uniqItems);
  }, []);

  const defaultSort: SortModelType = {
    colId : 'createdOn',
    sort  : 'desc'
  };

  const attributeMappings = {
    createdOnUI : 'createdOn'
  };
  
  useEffect(() => {
    if (isActive) {
      setRefreshRequired(true);
    }
  }, [isActive]);

  useEffect(() => {
    gridApi?.hideOverlay();
    if (gridApi && !hasItems) {
      gridApi.showNoRowsOverlay();
    }
  }, [hasItems, gridApi]);

  const onColumnChooserClose = useCallback(function(): void {
    toggleColumnChooser(false);
  }, []);

  const getGridHeight = useCallback(function() {
    return classes.filesGrid;
  }, []);

  return (
    <>
      {device?.id && (
        <IonGrid omitHeader
          ref={ionGridRef}
          paginationEnabled
          showCustomPaginationBar
          loadItems={getAlertHistory}
          augmentItems={constructAlertHistoryGridRows}
          attributeMappings={attributeMappings}
          columnDefinitions={columnDefinitions}
          defaultDefinitions={defaultDefinitions}
          showColumnChooser={showColumnChooser}
          onColumnChooserSave={onColumnSelectionsUpdate}
          onColumnChooserClose={onColumnChooserClose}
          defaultSort={defaultSort}
          refreshRequired={refreshRequired}
          setRefreshRequired={setRefreshRequired}
          showHeaderCheckbox={false}
          viewSubGroups={false}
          onGridReady={onGridReady}
          getGridHeight={getGridHeight}
          gridOptions={{onSelectionChanged}}
          columnComponents={{
            rowMenu : item => (
              <AlertRowEditor alert={item?.data} />
            ),
            alertConfigCell : (item: GenericObject) => (
              <LinkCell data={item}
                onClick={function() {
                  onAlertConfigClick(item?.data);
                }} />
            )
          }}
          pageOffset
          pageSizes={PAGE_SIZES} />
      )}
    </>
  );
};

/**
 * Returns column definitions for desktop alert history grid
 */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [{
    headerName         : translate('admin-devices:view.alertHistory.grid.alertConfiguration'),
    field              : 'alertConfigUI',
    cellRenderer       : 'alertConfigCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  },
  {
    headerName : translate('admin-devices:view.alertHistory.grid.createdOn'),
    field      : 'createdOnUI',
    sortable   : true,
    sort       : 'desc',
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.alertHistory.grid.acknowledgedBy'),
    field      : 'acknowledgedBy',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.alertHistory.grid.acknowledgedOn'),
    field      : 'acknowledgedOnUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.alertHistory.grid.acknowledged'),
    field      : 'acknowledgedUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};

/**
 * Returns column definitions for mobile alert history grid
 */
const getColumnDefinitionsMobile = (): ColDef[] => {
  return [{
    headerName         : translate('admin-devices:view.alertHistory.grid.alertConfiguration'),
    field              : 'alertConfigUI',
    cellRenderer       : 'alertConfigCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  },
  {
    headerName : translate('admin-devices:view.alertHistory.grid.createdOn'),
    field      : 'createdOnUI',
    sortable   : true,
    sort       : 'desc',
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.alertHistory.grid.acknowledgedBy'),
    field      : 'acknowledgedBy',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.alertHistory.grid.acknowledgedOn'),
    field      : 'acknowledgedOnUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.alertHistory.grid.acknowledged'),
    field      : 'acknowledgedUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};
const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});
export default forwardRef(AlertHistoryGrid);
